import { useState } from "react";

import { useRouter } from "next/router";
import {
  RegisterOptions,
  useController,
  useFormContext,
} from "react-hook-form";

import {
  useSetCustomisation,
  useGetCustomisation,
} from "queries/legacy/product-queries";
import { RadioGroupSchemaField } from "types/schema";
import { useInterpolate } from "utils/prismic-utils";

import MultiLineText from "../multiLineText/MultiLineText";
import RadioButton from "../radioButton/RadioButton";
import SingleLineText from "../singleLineText/SingleLineText";

import styles from "./RadioGroup.module.scss";

export type RadioGroupLayout = "inline" | "cards";
export type RadioGroupVariation =
  | "default"
  | "withFreeEntry"
  | "asCards"
  | "asCardsWithChooseImage";

export type FreeEntryProps = {
  component: RadioGroupSchemaField["metas"][0]["freeEntryComponent"];
  label: string;
  maxLength?: number;
  placeholder?: string;
};

type RadioGroupProps = {
  desktopColumns?: number;
  freeEntryProps?: FreeEntryProps;
  layout?: RadioGroupLayout;
  mobileColumns?: number;
  name: string;
  options: Record<string, string> | string[];
  registerOptions: RegisterOptions;
  upsells?: RadioGroupSchemaField["metas"][0]["upsells"];
  variation?: RadioGroupVariation;
};

/**
 * Group of radio buttons in inline or card layout
 */
export default function RadioGroup({
  mobileColumns = 2,
  desktopColumns = mobileColumns,
  freeEntryProps,
  layout = "inline",
  name,
  options,
  registerOptions,
  variation = "default",
  upsells,
}: Readonly<RadioGroupProps>) {
  const { data: customisation } = useGetCustomisation();
  const { mutate: setCustomisation } = useSetCustomisation();

  const { control } = useFormContext();
  const {
    field: { onChange, value, ...field },
    fieldState: { error },
  } = useController({ control, name, rules: registerOptions });

  const interpolate = useInterpolate();

  // Only set if defaultValue isn't an option
  const [freeEntryValue, setFreeEntryValue] = useState(
    (Array.isArray(options) ? options : Object.keys(options)).find(
      (o) => o === value
    )
      ? ""
      : value || ""
  );

  const router = useRouter();

  // Don't allow any changes to RadioGroup fields with upsells when editing an order
  const isDisabled = !!(upsells && router.query.order_id);

  const getFormattedUpsellValue = (key: string) => {
    if (!upsells) return "";

    const price = upsells.find((u) => u.id === key)?.price.formatted || "0.00";

    return `(+ ${price})`;
  };

  return (
    <div
      aria-invalid={error && value === undefined}
      className={layout === "cards" ? styles.cardsGroup : styles.group}
      role="radiogroup"
      style={
        {
          "--desktop-columns": desktopColumns,
          "--mobile-columns": mobileColumns,
        } as any
      }
    >
      {Array.isArray(options)
        ? options.map((id) => (
            <RadioButton
              key={id}
              checked={value === id}
              disabled={isDisabled}
              id={id}
              label={`${interpolate(id)} ${getFormattedUpsellValue(id)}`}
              layout={layout}
              value={id}
              onChange={
                variation === "asCardsWithChooseImage"
                  ? () => {
                      onChange(id);
                      setCustomisation({
                        data: { ...customisation, [name]: id },
                      });
                    }
                  : onChange
              }
              {...field}
            />
          ))
        : Object.entries(options).map(([id, label]) => (
            <RadioButton
              key={id}
              checked={value === id}
              disabled={isDisabled}
              id={id}
              label={`${interpolate(label)} ${getFormattedUpsellValue(id)}`}
              layout={layout}
              value={id}
              onChange={
                variation === "asCardsWithChooseImage"
                  ? () => {
                      onChange(id);
                      setCustomisation({
                        data: { ...customisation, [name]: id },
                      });
                    }
                  : onChange
              }
              {...field}
            />
          ))}

      {freeEntryProps && (
        <RadioButton
          checked={value === freeEntryValue}
          label={freeEntryProps.label}
          layout={layout}
          name={name}
          onChange={() => onChange(freeEntryValue || "")}
          value={freeEntryValue}
          freeEntryComponent={
            freeEntryProps.component === "single_line_text" ? (
              <SingleLineText
                aria-invalid={error && value === freeEntryValue}
                disabled={isDisabled}
                placeholder={freeEntryProps.placeholder}
                value={freeEntryValue}
                onChange={(event) => {
                  setFreeEntryValue(event.target.value);
                  onChange(event);
                }}
                {...field}
              />
            ) : (
              <MultiLineText
                aria-invalid={error && value === freeEntryValue}
                disabled={isDisabled}
                maxLength={freeEntryProps.maxLength}
                placeholder={freeEntryProps.placeholder}
                value={freeEntryValue}
                onChange={(event) => {
                  setFreeEntryValue(event.target.value);
                  onChange(event);
                }}
                {...field}
              />
            )
          }
        />
      )}
    </div>
  );
}
